import {
  GetVirtualDashboardRequest,
  MetricDetail,
  VirtualDashboardId,
  VirtualObjectSize,
  VirtualObjectFormat,
  FunnelDetail,
  NoteSettings,
  HomeCard,
  VirtualObject as VirtualObjectProto,
  RetentionMetricDetail,
} from '@fullstory/proto/fs/services/dx-objects';
import {
  AppBackendRequestActions,
  AppDispatch,
  fullstorySelectors,
  getRevenueEventRequestState,
  ImpactRequests,
} from '@fullstory/redux';
import { useXhrRequest } from '@fullstory/redux-util';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Funnel, Metric, RetentionMetric } from '@fullstory/proto/fs/services/search-settings';
import { useRequestState } from '@fullstory/redux/utils/hooks';
import { omit } from 'lodash';
import { RevenueEvent } from '@fullstory/proto/fs/services/webber/conversions';
import { UID, WithID } from '@fullstory/util/uid';
import { useFlags } from '@fullstory/react-hooks';
import { getOrgId } from '@fullstory/redux/modules/fullstory/selectors';
import { Flag } from '@fullstory/proto/fs/shared/flags';

const toMetricDetail = (metric: Metric | null | undefined): MetricDetail | undefined => {
  if (!metric) return undefined;

  return {
    metric: {
      metric,
      metadata: null,
      isDcmCreated: false,
      isCanned: false,
    },
    starred: false,
    lastViewed: null,
    canned: false,
  };
};

const toFunnelDetail = (funnel: Funnel | null | undefined): FunnelDetail | undefined => {
  if (!funnel) return undefined;

  return {
    funnel: {
      funnel,
      metadata: null,
      isDcmCreated: false,
    },
    starred: false,
    lastViewed: null,
  };
};

const toRetentionMetricDetail = (
  retentionMetric: RetentionMetric | null | undefined,
): RetentionMetricDetail | undefined => {
  if (!retentionMetric) return undefined;

  return {
    retentionMetric: {
      retentionMetric,
      metadata: null,
    },
    starred: false,
    lastViewed: null,
  };
};
export type UseVirtualDashboardParams = Omit<GetVirtualDashboardRequest, 'id' | 'orgId'>;
type VirtualObject = {
  metricDetail?: MetricDetail;
  funnelDetail?: FunnelDetail;
  funnelMetricType?: HomeCard.FunnelMetricType;
  note?: NoteSettings;
  retentionMetricDetail?: RetentionMetricDetail;
  id: UID;
  size: VirtualObjectSize;
  format: VirtualObjectFormat;
};

const paramsWithRevenueEvent = (
  params: UseVirtualDashboardParams,
  revenueEvent: RevenueEvent | null,
) => {
  if (!params.email || !revenueEvent) return params;

  // if we have the user email and revenue event, send it as the "usersUser" input
  return {
    ...omit(params, 'email'),
    usersUser: {
      email: params.email,
      customEvent: revenueEvent.eventName,
      definedEvent: revenueEvent.definedEventId,
      eventProperty: revenueEvent.eventProperty,
      currency: revenueEvent.currency,
    },
  };
};

// duplicates logic from useRevenueEventEffect hook in conversions to remove dependency
export const useRevenueEventEffect = (): void => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const flags = useFlags();

  useEffect(() => {
    if (flags.hasBit(Flag.FeatImpacts)) {
      dispatch(ImpactRequests.getRevenueEvent(orgId));
    }
  }, [dispatch, orgId, flags]);
};

export const useVirtualObjects = (
  id: VirtualDashboardId,
  params: UseVirtualDashboardParams,
  idOnlyRequest?: boolean,
): {
  objects: VirtualObject[];
  error: boolean;
  isReady: boolean;
  loading: boolean;
  lastRequested: number;
} => {
  const orgId = useSelector(fullstorySelectors.getOrgId);

  // TODO - it would be better to await the revenue event load to avoid any layout shift
  // that would happen after re-requesting the virtual dashboard after the revenue event returns.
  // but even that fetch is conditional so useRevenueEventEffect becomes a leaky abstraction,
  // and only some dashboards even use the revenue event.
  // revisit this later!! For now, just fetch the VD multiple times if the revenue event comes back later.
  const { data: revenueEvent } = useRequestState(getRevenueEventRequestState);
  useRevenueEventEffect();

  const hasParams = useMemo(() => Object.values(params).some(v => !!v), [params]);
  const request = useMemo(
    () =>
      hasParams || idOnlyRequest
        ? AppBackendRequestActions.getVirtualDashboard({
            orgId,
            id,
            ...paramsWithRevenueEvent(params, revenueEvent),
          })
        : null,
    [hasParams, idOnlyRequest, orgId, id, params, revenueEvent],
  );
  const { data, error, isReady, loading, lastRequested } = useXhrRequest(request);

  const objects = useMemo((): VirtualObject[] => {
    // if parameters are emptied out, an updated request will not be made,
    // the data representing objects is now stale, and should be cleared.
    if (!hasParams && !idOnlyRequest) return [];

    return (data?.dashboard?.objects ?? []).map(o => ({
      metricDetail: toMetricDetail(o.metric),
      funnelDetail: toFunnelDetail(o.funnel),
      funnelMetricType: o.funnelMetricType,
      note: o.note ?? undefined,
      retentionMetricDetail: toRetentionMetricDetail(o.retentionMetric),
      size: o.size,
      id: o.id,
      format: o.format,
    }));
  }, [data?.dashboard?.objects, hasParams, idOnlyRequest]);

  return { objects, error, isReady, loading, lastRequested };
};

// returns just the virtual objects in their proto form for a virtual dashboard id
export const useVirtualProtoObjects = (id: VirtualDashboardId): WithID<VirtualObjectProto>[] => {
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const request = useMemo(
    () =>
      AppBackendRequestActions.getVirtualDashboard({
        orgId,
        id,
        overlay: null,
      }),
    [id, orgId],
  );
  const { data } = useXhrRequest(request);

  return data?.dashboard?.objects ?? [];
};
